// -----------------------------------------------------------------------------
// CandidaturesManagement.jsx  ⎯ barre “statuts” sous la zone de recherche
// -----------------------------------------------------------------------------

import React, { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Modal,
  Box,
  Button,
  Alert,
  TextField,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  Edit as EditIcon,
  SwapVert as SwapVertIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Close as CloseIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  fetchAllCandidatures,
  updateCandidatures,
  fetchSortedCandidatures,
  searchCandidatures,
  fetchCandidaturesByAppelOffreOwner,
} from "../../redux/candidatureSlice";
import { getColorForStatus } from "../../utils/utils";
import ProfileCard from "../Profiles/ProfileCard";
import ProfileDetails from "../Profiles/ProfileDetails";
import ConfirmationActionModal from "../Common/ConfirmationActionModal";
import CustomLoader from "../Common/CustomLoader";
import "./styles/CandidaturesManagement.css";

const statusOptions = [
  { value: "", label: "Tous" },
  { value: "retenue", label: "Retenue" },
  { value: "refusée", label: "Refusée" },
  { value: "en attente", label: "En attente" },
];

const CandidaturesManagement = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { candidatures, loading, error } = useSelector(
    (state) => state.candidatures
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [pendingStatus, setPendingStatus] = useState(null);

  const handleProfileCardClick = () => {
    setOpenProfileModal(false);
    setTimeout(() => setOpenProfileModal(true), 0); // Reopen modal to show details
  };

  /* ----------------------------------------------------------------------- */
  /*  Lifecycle                                                              */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    dispatch(fetchCandidaturesByAppelOffreOwner());
  }, [dispatch]);

  /* ----------------------------------------------------------------------- */
  /*  Handlers                                                               */
  /* ----------------------------------------------------------------------- */
  const handleOpenEditModal = (c) => {
    setSelectedCandidature(c);
    setOpenEditModal(true);
  };
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedCandidature(null);
  };

  const handleStatusUpdate = (s) => {
    if (!selectedCandidature) return;
    dispatch(updateCandidatures({ id: selectedCandidature.id, status: s })).then(
      () => {
        setSuccessMessage("Statut mis à jour avec succès");
        setTimeout(() => setSuccessMessage(""), 3000);
        dispatch(fetchCandidaturesByAppelOffreOwner());
      }
    );
  };

  const handleOpenConfirmationModal = (s) => {
    setPendingStatus(s);
    setConfirmationMessage(
      `Êtes‑vous sûr de vouloir ${
        s === "retenue" ? "accepter" : "refuser"
      } la candidature ?`
    );
    setConfirmationModalOpen(true);
  };
  const handleConfirmAction = () => {
    if (pendingStatus) handleStatusUpdate(pendingStatus);
    setConfirmationModalOpen(false);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleOpenProfileModal = (p) => {
    setSelectedProfile(p);
    setOpenProfileModal(true);
  };
  const handleCloseProfileModal = () => {
    setSelectedProfile(null);
    setOpenProfileModal(false);
  };

  const handleProfileClick = (profile) => {
    setSelectedProfile(profile);
  };

  const handleSortToggle = () => {
    const newOrder = sortOrder === "DESC" ? "ASC" : "DESC";
    setSortOrder(newOrder);
    dispatch(
      fetchSortedCandidatures({ sort: sortField, order: newOrder, status: statusFilter })
    );
  };

  const applyStatusFilter = (value) => {
    setStatusFilter(value);
    dispatch(
      fetchSortedCandidatures({ sort: sortField, order: sortOrder, status: value })
    );
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      dispatch(searchCandidatures(searchQuery))
        .unwrap()
        .catch((err) => {
          setSuccessMessage("");
          setErrorMessage(err);
        });
    } else dispatch(fetchAllCandidatures());
  };

  /* ----------------------------------------------------------------------- */
  /*  Loading                                                                */
  /* ----------------------------------------------------------------------- */
  if (loading) {
    return (
      <Container className="loading-screen">
        <CustomLoader />
      </Container>
    );
  }

  /* ----------------------------------------------------------------------- */
  /*  Render                                                                 */
  /* ----------------------------------------------------------------------- */
  return (
    <Container className="candidatures-container">
      <Typography
        variant="h4"
        className="candidatures-title"
        sx={{ mb: 4, fontWeight: "bold", color: "#2F4E8B" }}
      >
        Gestion des Candidatures
      </Typography>

      {/* ───────────────────── SEARCH + STATUS CHIPS ────────────────────── */}
      <Box className="candidatures-searchbar" sx={{ mb: 3 }}>
        <TextField
          size="small"
          placeholder="Rechercher par titre d'Appel d'Offre"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          className="search-input"
          InputProps={{
            sx: {
              borderRadius: 3,
              "& fieldset": { borderColor: "#2F4E8B !important" },
              "&:hover fieldset": { borderColor: "#2F4E8B !important" },
              "&.Mui-focused fieldset": { borderColor: "#2F4E8B !important" },
            },
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          className="search-btn"
        >
          Rechercher
        </Button>
      </Box>

      {/* Barre de filtres statuts sous la zone de recherche */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ mt: 2, mb: 3 }}
      >
        {statusOptions.map((opt) => (
          <Button
            key={opt.value}
            variant={statusFilter === opt.value ? "contained" : "outlined"}
            className={`status-filter-chip ${
              statusFilter === opt.value ? "selected" : ""
            }`}
            onClick={() => applyStatusFilter(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </Stack>

      {/* Alerts */}
      {(error || errorMessage) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === "string" ? error : errorMessage || "Une erreur est survenue"}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* ─────────────────────────── TABLE ─────────────────────────────── */}
      <TableContainer
        component={Paper}
        className="table-wrapper"
        sx={{
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px", // Added for horizontal scrollbar
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#2F4E8B", // Main blue color
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#0d1b3e", // Darker blue on hover
          },
          "&::-webkit-scrollbar-thumb:horizontal": { // Horizontal scrollbar
            backgroundColor: "#2F4E8B", // Main blue color
          },
          "&::-webkit-scrollbar-thumb:horizontal:hover": {
            backgroundColor: "#0d1b3e", // Darker blue on hover
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow className="table-head-row">
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Date
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Appel d’Offre
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Profil
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}
              >
                Statut
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Commentaire
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {candidatures
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  className="table-body-row"
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.9rem",
                      color: "#4a4a4a",
                    },
                  }}
                >
                  <TableCell>{c.createdAt || "N/A"}</TableCell>
                  <TableCell>{c.appelOffre?.titre || "N/A"}</TableCell>

                  <TableCell>
                    <span
                      className="profile-link"
                      onClick={() => handleOpenProfileModal(c.profile)}
                    >
                      {c.profile?.nom || "N/A"}
                    </span>
                  </TableCell>

                  <TableCell className="status-cell" align="center">
                    <span
                      className="status-chip"
                      style={{ backgroundColor: getColorForStatus(c.status) }}
                    >
                      {c.status || "Non spécifié"}
                    </span>
                  </TableCell>

                  <TableCell>{c.commentaire || "N/A"}</TableCell>

                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleOpenEditModal(c)}
                      className="action-icon"
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Custom Pagination */}
      {candidatures.length > rowsPerPage && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={4}
          gap={2}
        >
          <IconButton
            onClick={() => handleChangePage(null, page - 1)}
            disabled={page === 0}
          >
            <ArrowBackIosIcon sx={{ color: "#2F4E8B" }} />
          </IconButton>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#2F4E8B",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            {page + 1}
          </Box>
          <IconButton
            onClick={() => handleChangePage(null, page + 1)}
            disabled={(page + 1) * rowsPerPage >= candidatures.length}
          >
            <ArrowForwardIosIcon sx={{ color: "#2F4E8B" }} />
          </IconButton>
        </Box>
      )}

      {/* ──────────────────────── MODALES ──────────────────────────────── */}
      <Modal open={openEditModal}>
        <Box className="edit-modal" sx={{ position: "relative" }}>
          <IconButton
            onClick={handleCloseEditModal}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#4F5565",
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h5" className="modal-title">
            Mettre à jour la candidature
          </Typography>
          <Box className="edit-actions">
            <Button
              className="success-btn"
              onClick={() => handleOpenConfirmationModal("retenue")}
            >
              Retenue
            </Button>
            <Button
              className="danger-btn"
              onClick={() => handleOpenConfirmationModal("refusée")}
            >
              Refusée
            </Button>
          </Box>
        </Box>
      </Modal>

      <ConfirmationActionModal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        onConfirm={handleConfirmAction}
        message={confirmationMessage}
      />

      <Modal open={openProfileModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: 1400,
            maxHeight: "90vh",
            overflowY: "auto",
            p: 5,
            borderRadius: 3,
            bgcolor: "#fff",
            boxShadow: 24,
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px", // Added for horizontal scrollbar
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#2F4E8B", // Main blue color
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#0d1b3e", // Darker blue on hover
            },
            "&::-webkit-scrollbar-thumb:horizontal": {
              backgroundColor: "#2F4E8B", // Main blue color
            },
            "&::-webkit-scrollbar-thumb:horizontal:hover": {
              backgroundColor: "#0d1b3e", // Darker blue on hover
            },
          }}
        >
          {/* Close icon */}
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <IconButton
              size="small"
              onClick={handleCloseProfileModal}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "#2F4E8B",
              mb: 4,
              textAlign: "center",
            }}
          >
            Profil de Candidature
          </Typography>

          {selectedProfile && (
            <>
              {!selectedProfile.detailsVisible ? (
                <Box
                  onClick={() => {
                    setSelectedProfile({ ...selectedProfile, detailsVisible: true });
                    handleProfileCardClick();
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <ProfileCard profile={selectedProfile} />
                </Box>
              ) : (
                <ProfileDetails profile={selectedProfile} />
              )}
            </>
          )}
        </Box>
      </Modal>
    </Container>
  );
};

export default CandidaturesManagement;
